import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@kidshome/ui';

/**
 * Service-worker update toast (ADR-0018, `registerType: 'prompt'`). When a new SW has installed and
 * is waiting, we show a non-intrusive "New version — Reload" toast instead of auto-reloading mid-
 * session (kind UX — never yank a kid/parent away from what they're doing). Reload activates the new
 * SW (`updateServiceWorker(true)` skips waiting and reloads). The SW itself only runs in build/
 * preview (`devOptions.enabled: false`), so this is inert in `vite dev`.
 */
/** How often to poll for a waiting SW. An installed standalone PWA a family keeps open for days would
 * otherwise only discover a new version on a full reload — so the "new version" toast could never fire. */
const UPDATE_POLL_MS = 60 * 60 * 1000;

export function UpdatePrompt(): React.ReactElement | null {
  const { t } = useTranslation(['pwa', 'common']);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Poll for a waiting update so `needRefresh` can flip mid-session (the toast is rendered only when
      // it does). Guarded on `onLine` so an offline tick doesn't throw; the interval lives for the app's
      // lifetime (UpdatePrompt is a root-level singleton, like the toast host — never unmounts).
      setInterval(() => {
        if (navigator.onLine) void registration.update();
      }, UPDATE_POLL_MS);
    },
  });

  if (!needRefresh) return null;

  return (
    <div
      role="alert"
      className="fixed inset-x-4 bottom-[calc(var(--bottom-nav-height)+1.25rem)] z-[60] mx-auto flex max-w-md items-center gap-3 rounded-card border border-border bg-surface p-4 shadow-lg"
    >
      <div className="flex min-w-0 flex-col">
        <span className="font-display text-sm font-bold text-text">{t('update.title')}</span>
        <span className="truncate text-xs text-text-muted">{t('update.message')}</span>
      </div>
      <div className="ms-auto flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setNeedRefresh(false)}>
          {t('common:actions.close')}
        </Button>
        <Button variant="primary" size="sm" onClick={() => void updateServiceWorker(true)}>
          {t('pwa:update.action')}
        </Button>
      </div>
    </div>
  );
}
